import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API, fetchWebhooks } from '../api'
import Button from '../components/Button'
import TopBar from '../components/Topbar'
import {
  Webhook,
  getUserState,
  setWebhooks,
  useDispatch,
  useSelector,
} from '../store'

export const Webhooks = () => {
  const dispatch = useDispatch()
  const { webhooks } = useSelector(getUserState)

  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | undefined>(
    undefined
  )
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchWebhooks()
  }, [])

  const confirmDeletion = async () => {
    setDeleting(true)
    await API(await fetchAuthSession()).delete(
      `/webhook/${selectedWebhook?.id}`
    )
    dispatch(
      setWebhooks(
        webhooks.filter((webhook) => webhook?.id !== selectedWebhook?.id)
      )
    )
    setSelectedWebhook(undefined)
    setDeleting(false)
  }

  return (
    <>
      <Dialog
        open={!!selectedWebhook}
        onClose={() => !deleting && setSelectedWebhook(undefined)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto w-[50rem] rounded-lg p-20 bg-primary-50">
            <DialogTitle className="text-center text-[22px] mb-4">
              Delete Webhook
            </DialogTitle>
            <Description className="py-4 text-center text-[18px] mb-4">
              Are you sure you wish to delete&nbsp;
              <span className="font-semibold">
                &quot;
                {selectedWebhook?.name}
                &quot;
              </span>
              ?
            </Description>

            <div className="w-1/2 mx-auto">
              <div className="grid grid-cols-2 gap-2">
                <div className="w-full items-center">
                  <button
                    className={`block w-full button button-large button-light py-2 px-4 my-2 ${deleting ? 'disabled' : ''}`}
                    onClick={() => !deleting && setSelectedWebhook(undefined)}
                  >
                    <div className="button button-label">No, cancel</div>
                  </button>
                </div>
                <div className="w-full justify-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-dark py-2 px-4 my-2"
                    onClick={confirmDeletion}
                    disabled={deleting}
                  >
                    <div className="button button-label">
                      {deleting ? 'Loading...' : 'Yes, delete'}
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
      <TopBar title="Webhooks" description="Manage your webhooks." />
      <div className="w-full flex mb-5 justify-between items-center">
        <h3 className="font-semibold text-[20px]">
          Webhooks
          {` - ${(webhooks ?? []).length}`}
        </h3>
        <Button
          as={Link}
          to="/webhooks/create"
          label="Create new subscription"
          className="text-center"
        />
      </div>
      <div className="w-full rounded-xl border border-gray-400 p-8">
        <table className="table-auto w-full border-spacing-3 access-token-table text-[14px]">
          <thead className="text-left font-normal">
            <tr>
              <th className="font-thin">Webhook name</th>
              <th className="font-thin">Callback URL</th>
              <th className="font-thin">View logs</th>
              <th className="font-thin">Delete</th>
            </tr>
          </thead>
          <tbody className="font-medium">
            {(webhooks ?? []).length === 0 && (
              <tr>
                <td colSpan={4}>
                  You do not have any Webhook.
                  <div className="w-60 mx-auto">
                    <Link to="/webhooks/create">
                      <Button
                        label="Create new subscription"
                        className="w-60 my-4 text-center"
                      />
                    </Link>
                  </div>
                </td>
              </tr>
            )}
            {(webhooks ?? []).map((webhook) => (
              <tr key={webhook.id}>
                <td>{webhook.name}</td>
                <td>{webhook.url}</td>
                <td>
                  <Link to={`/webhooks/${webhook.id}/logs`}>
                    <button type="button" className="colored-link font-normal">
                      Logs
                    </button>
                  </Link>
                </td>
                <td>
                  <button
                    type="button"
                    className="colored-link font-normal"
                    onClick={() => setSelectedWebhook(webhook)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
